import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { getInitials } from '../../lib/utils';
import { format } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { 
  Clock, Users, ClipboardCheck, Palmtree, Play, TrendingUp, TrendingDown,
  ChevronRight, UserPlus, FolderPlus, CheckCircle2, Download,
  AlertTriangle, AlertCircle, Send, Eye, RefreshCw, Activity, Target, Zap,
  Calendar, ArrowUpRight, ArrowDownRight, Minus, ArrowRight, ChevronDown, ChevronUp,
  Trophy, AlertOctagon, Camera, MonitorSmartphone, Crown
} from 'lucide-react';
import SetupWizard from '../../components/SetupWizard';
import { useSubscription } from '../../components/SubscriptionGuard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatNameCompact = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
};

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16'];

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [stats, setStats] = useState(null);
  const [trackingNow, setTrackingNow] = useState([]);
  const [pendingActions, setPendingActions] = useState({ actions: [], counts: {} });
  const [chartData, setChartData] = useState({ chart_data: [], projects: [] });
  const [employees, setEmployees] = useState([]);
  const [projects, setProjects] = useState([]);
  const [todayData, setTodayData] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [recentScreenshots, setRecentScreenshots] = useState([]);
  const [screenshotUrls, setScreenshotUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [chartWeek, setChartWeek] = useState('this');
  const [chartProjectFilter, setChartProjectFilter] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [workspaceSettings, setWorkspaceSettings] = useState(null);
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAlertDetails, setShowAlertDetails] = useState(false);
  const [insightsTab, setInsightsTab] = useState('top');
  const { subscription } = useSubscription() || {};
  const isFreePlan = subscription?.plan === 'free';

  useEffect(() => {
    if (user && !['admin', 'manager'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Check if setup wizard should be shown (admin only)
  useEffect(() => {
    if (!token || !user || user.role !== 'admin') return;
    fetch(`${API_URL}/api/admin/workspace-settings`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setWorkspaceSettings(data);
        if (!data.setup_completed) setShowSetupWizard(true);
      })
      .catch(console.error);
  }, [token, user]);

  const fetchDashboardData = useCallback(async () => {
    if (!token) return;
    try {
      const [statsRes, trackingRes, pendingRes, employeesRes, projectsRes, todayRes, productivityRes, alertsRes, topPerfRes, needsAttnRes, screenshotsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/tracking-now`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/pending-actions`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/today`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/productivity`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/alerts`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/top-performers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/needs-attention`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/dashboard/recent-screenshots`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const [statsData, trackingData, pendingData, employeesData, projectsData, todayDataRes, productivityData, alertsData, topPerfData, needsAttnData, screenshotsData] = await Promise.all([
        statsRes.json(), trackingRes.json(), pendingRes.json(), employeesRes.json(),
        projectsRes.json(), todayRes.json(), productivityRes.json(), alertsRes.json(),
        topPerfRes.json(), needsAttnRes.json(), screenshotsRes.json()
      ]);
      setStats(statsData);
      setTrackingNow(trackingData);
      setPendingActions(pendingData);
      setEmployees(employeesData);
      setProjects(projectsData);
      setTodayData(todayDataRes);
      setProductivity(productivityData);
      setAlerts(alertsData);
      setTopPerformers(topPerfData);
      setNeedsAttention(needsAttnData);
      setRecentScreenshots(screenshotsData);
      // Fetch authenticated thumbnails for dashboard screenshots
      screenshotsData.forEach(async (ss) => {
        try {
          const resp = await fetch(`${API_URL}${ss.thumbnail_url}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.ok) {
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            setScreenshotUrls(prev => ({ ...prev, [ss.id]: url }));
          }
        } catch {}
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  }, [token]);

  const fetchChartData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard/activity-chart?week=${chartWeek}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setChartData(data);
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
    }
  }, [token, chartWeek]);

  useEffect(() => {
    setLoading(true);
    fetchDashboardData().finally(() => setLoading(false));
  }, [fetchDashboardData, refreshKey]);

  useEffect(() => { fetchChartData(); }, [fetchChartData]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetch(`${API_URL}/api/admin/dashboard/tracking-now`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => res.json()).then(data => setTrackingNow(data)).catch(console.error);
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleSendReminder = async (employeeIds, weekStart) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/approvals/send-reminder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ employee_ids: Array.isArray(employeeIds) ? employeeIds : [employeeIds], week_start: weekStart || new Date().toISOString().slice(0, 10) })
      });
      const data = await res.json();
      if (res.ok) toast.success(data.message || 'Reminder sent');
      else toast.error(data.detail || 'Failed to send reminder');
    } catch (error) { toast.error('Failed to send reminder'); }
  };

  const handleApproveAllTimesheets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/dashboard/approve-all-timesheets`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(data.message);
      setRefreshKey(k => k + 1);
    } catch (error) { toast.error('Failed to approve timesheets'); }
  };

  const handleExportWeekReport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/export/csv?report_type=by-employee&range=week`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `weekly_report_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('Report downloaded');
    } catch (error) { toast.error('Failed to export report'); }
  };

  const formatElapsed = (seconds) => {
    const secs = Math.max(0, Math.floor(seconds));
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const remainingSecs = secs % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    if (mins > 0) return `${mins}m ${remainingSecs}s`;
    return `${remainingSecs}s`;
  };

  const formatHoursMinutes = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return '0h 0m';
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    return `${hrs}h ${mins}m`;
  };

  const formatDecimalHours = (decimalHours) => {
    if (!decimalHours || decimalHours <= 0) return '0h 0m';
    const totalSeconds = Math.round(decimalHours * 3600);
    return formatHoursMinutes(totalSeconds);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700';
      case 'submitted': return 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700';
      case 'rejected': return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700';
      default: return 'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-200 border-slate-200 dark:border-slate-500';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboard.goodMorning');
    if (hour < 18) return t('dashboard.goodAfternoon');
    return t('dashboard.goodEvening');
  };

  const cardStyle = { boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]" data-testid="admin-dashboard-loading">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-400 font-medium">{t('common.loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {showSetupWizard && (
        <SetupWizard
          settings={workspaceSettings}
          onComplete={() => setShowSetupWizard(false)}
          isFreePlan={isFreePlan}
        />
      )}
      <div className="space-y-8" data-testid="admin-dashboard-page">
        {/* Hero Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase">{getGreeting()}</p>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              {user?.role === 'manager' ? t('dashboard.teamOverview') : t('dashboard.companyOverview')}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {format(new Date(), 'EEEE, MMMM d, yyyy')}
              {stats?.active_employees_today > 0 && (
                <span className="text-slate-400"> &middot; {stats.active_employees_today} employee{stats.active_employees_today !== 1 ? 's' : ''} active today</span>
              )}
            </p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setRefreshKey(k => k + 1)}
            className="rounded-lg h-9 text-xs font-semibold self-start sm:self-auto"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            {t('common.refresh')}
          </Button>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats-row">
          <Link to="/admin/reports">
            <div className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 group hover:shadow-md transition-shadow" style={cardStyle} data-testid="stat-hours">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 dark:bg-blue-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                {stats?.week_trend_pct !== null && stats?.week_trend_pct !== undefined && (
                  <span className={`flex items-center gap-0.5 text-xs font-semibold ${stats.week_trend_pct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {stats.week_trend_pct >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                    {Math.abs(stats.week_trend_pct)}%
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white timer-display tracking-tight">
                {stats?.total_seconds_this_week ? formatHoursMinutes(stats.total_seconds_this_week) : formatDecimalHours(stats?.total_hours_this_week)}
              </p>
              <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">{t('dashboard.totalHoursThisWeek')}</p>
            </div>
          </Link>

          <Link to="/admin/people">
            <div className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5 group hover:shadow-md transition-shadow" style={cardStyle} data-testid="stat-employees">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 dark:bg-emerald-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{stats?.total_employees || 0}</p>
              <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">
                {user?.role === 'manager' ? t('dashboard.directReports') : t('dashboard.activeEmployees')}
              </p>
              {stats?.active_employees_today > 0 && (
                <p className="text-xs text-emerald-600 font-medium mt-1">{stats.active_employees_today} tracked today</p>
              )}
            </div>
          </Link>

          <Link to="/admin/approvals">
            <div className={`relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border p-5 group hover:shadow-md transition-shadow ${
              stats?.pending_approvals > 0 ? 'border-amber-200 dark:border-amber-500/30' : 'border-slate-200/60 dark:border-slate-700'
            }`} style={cardStyle} data-testid="stat-approvals">
              <div className="absolute top-0 right-0 w-24 h-24 bg-amber-50 dark:bg-amber-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center group-hover:bg-amber-100 transition-colors ${
                  stats?.pending_approvals > 0 ? 'bg-amber-50 dark:bg-amber-900/30' : 'bg-slate-50 dark:bg-slate-700'
                }`}>
                  <ClipboardCheck className={`w-5 h-5 ${stats?.pending_approvals > 0 ? 'text-amber-600' : 'text-slate-500 dark:text-slate-400'}`} />
                </div>
                {stats?.pending_approvals > 0 && (
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-pulse" />
                )}
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{stats?.pending_approvals || 0}</p>
              <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">{t('dashboard.pendingApprovals')}</p>
            </div>
          </Link>

          <Link to="/admin/time-off">
            <div className={`relative overflow-hidden bg-white dark:bg-slate-800 rounded-xl border p-5 group hover:shadow-md transition-shadow ${
              stats?.pending_time_off > 0 ? 'border-violet-200 dark:border-violet-500/30' : 'border-slate-200/60 dark:border-slate-700'
            }`} style={cardStyle} data-testid="stat-timeoff">
              <div className="absolute top-0 right-0 w-24 h-24 bg-violet-50 dark:bg-violet-900/5 rounded-bl-[60px] -mr-4 -mt-4" />
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center group-hover:bg-violet-100 transition-colors ${
                  stats?.pending_time_off > 0 ? 'bg-violet-50 dark:bg-violet-900/30' : 'bg-slate-50 dark:bg-slate-700'
                }`}>
                  <Palmtree className={`w-5 h-5 ${stats?.pending_time_off > 0 ? 'text-violet-600' : 'text-slate-500 dark:text-slate-400'}`} />
                </div>
                {stats?.pending_time_off > 0 && (
                  <span className="w-2.5 h-2.5 rounded-full bg-violet-400 animate-pulse" />
                )}
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">{stats?.pending_time_off || 0}</p>
              <p className="text-xs font-medium text-slate-400 mt-1 uppercase tracking-wider">{t('dashboard.pendingTimeOff')}</p>
            </div>
          </Link>
        </div>

        {/* Quick Actions */}
        {user?.role === 'admin' && (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-4" style={cardStyle}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('dashboard.quickActions')}</span>
              <div className="w-px h-5 bg-slate-200" />
              <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs font-semibold" onClick={() => navigate('/admin/people')} data-testid="action-invite">
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard.inviteEmployee')}
              </Button>
              <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs font-semibold" onClick={() => navigate('/admin/projects')} data-testid="action-project">
                <FolderPlus className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard.createProject')}
              </Button>
              {stats?.pending_approvals > 0 && (
                <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs font-semibold" onClick={handleApproveAllTimesheets} data-testid="action-approve-all">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard.approveAllPending')}
                </Button>
              )}
              <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs font-semibold" onClick={handleExportWeekReport} data-testid="action-export">
                <Download className="w-3.5 h-3.5 mr-1.5" /> {t('dashboard.exportThisWeek')}
              </Button>
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-6">
            {/* Who Is Tracking Now */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 overflow-hidden" style={cardStyle}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('dashboard.whoIsTracking')}</h3>
                  {trackingNow.length > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">{trackingNow.length}</span>
                  )}
                </div>
                <span className="text-xs text-slate-400">{t('dashboard.updatesEvery30s')}</span>
              </div>
              <div className="p-4">
                {trackingNow.length > 0 ? (
                  <div className="space-y-2">
                    {trackingNow.map(tracker => (
                      <div key={tracker.entry_id} className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/30/60 rounded-xl" data-testid={`tracking-${tracker.entry_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <Avatar className="h-9 w-9">
                              {tracker.employee.profile_photo && <AvatarImage src={tracker.employee.profile_photo} />}
                              <AvatarFallback className="bg-emerald-600 text-white text-xs font-semibold">
                                {getInitials(tracker.employee.name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white animate-pulse" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white">{tracker.employee.name}</p>
                            <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                              {tracker.project && (
                                <>
                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: tracker.project.color }} />
                                  <span>{tracker.project.name}</span>
                                </>
                              )}
                              {!tracker.project && <span className="italic">No project</span>}
                            </div>
                          </div>
                        </div>
                        <p className="text-base font-bold timer-display text-emerald-600">{formatElapsed(tracker.elapsed_seconds)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="w-8 h-8 mx-auto text-slate-200 mb-2" />
                    <p className="text-sm text-slate-400">{t('dashboard.noOneTracking')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Team Activity Chart */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-6" style={cardStyle}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('dashboard.teamActivity')}</h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Total: {(() => {
                      const filteredProjects = chartProjectFilter === 'all'
                        ? chartData.projects?.map(p => p.name) || []
                        : [chartProjectFilter];
                      return chartData.chart_data?.reduce((sum, day) => {
                        return sum + Object.entries(day).reduce((daySum, [key, val]) =>
                          key !== 'date' && key !== 'day' && filteredProjects.includes(key) ? daySum + (val || 0) : daySum, 0);
                      }, 0).toFixed(1) || 0;
                    })()}h
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {chartData.projects?.length > 1 && (
                    <select
                      value={chartProjectFilter}
                      onChange={(e) => setChartProjectFilter(e.target.value)}
                      className="h-8 px-2.5 text-xs font-medium rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 cursor-pointer"
                      data-testid="chart-project-filter"
                    >
                      <option value="all">All Projects</option>
                      {chartData.projects.map(p => (
                        <option key={p.id} value={p.name}>{p.name}</option>
                      ))}
                    </select>
                  )}
                  <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
                    {[
                      { value: 'this', label: t('common.thisWeek') },
                      { value: 'last', label: t('common.lastWeek') }
                    ].map((f) => (
                      <button
                        key={f.value}
                        onClick={() => setChartWeek(f.value)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                          chartWeek === f.value ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="h-[300px]">
                {(() => {
                  const visibleProjects = chartProjectFilter === 'all'
                    ? chartData.projects || []
                    : chartData.projects?.filter(p => p.name === chartProjectFilter) || [];
                  const hasData = chartData.chart_data?.length > 0 && visibleProjects.length > 0 &&
                    chartData.chart_data.some(day => visibleProjects.some(p => day[p.name] > 0));
                  return !hasData ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <Activity className="w-8 h-8 text-slate-200 mb-2" />
                      <p className="text-sm text-slate-400">{t('dashboard.noDataPeriod')}</p>
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.chart_data}>
                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => `${v}h`} width={40} />
                        <Tooltip
                          formatter={(value) => [`${value}h`]}
                          contentStyle={{ backgroundColor: '#0f172a', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                          itemStyle={{ color: '#fff' }}
                          labelStyle={{ color: '#94a3b8', fontWeight: 600, marginBottom: '4px' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '12px' }} />
                        {visibleProjects.map((project, idx) => (
                          <Bar
                            key={project.id}
                            dataKey={project.name}
                            name={project.name}
                            fill={project.color || CHART_COLORS[idx % CHART_COLORS.length]}
                            stackId="stack"
                            radius={[4, 4, 0, 0]}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  );
                })()}
              </div>
            </div>

            {/* Productivity Insights */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 overflow-hidden" style={cardStyle} data-testid="productivity-insights">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-500" />
                  Productivity Insights
                </h3>
                <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-700 rounded-lg">
                  {[
                    { value: 'top', label: 'Top Performers' },
                    { value: 'attention', label: 'Needs Attention' }
                  ].map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setInsightsTab(tab.value)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                        insightsTab === tab.value
                          ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-700 dark:text-slate-300'
                      }`}
                      data-testid={`insights-tab-${tab.value}`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {insightsTab === 'top' ? (
                  topPerformers.length > 0 ? topPerformers.map((emp, idx) => (
                    <div key={emp.employee_id} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors" data-testid={`top-performer-${idx}`}>
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <Avatar className="h-8 w-8">
                            {emp.employee_photo && <AvatarImage src={emp.employee_photo} />}
                            <AvatarFallback className="bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs font-semibold">
                              {getInitials(emp.employee_name)}
                            </AvatarFallback>
                          </Avatar>
                          {idx === 0 && <Crown className="absolute -top-1.5 -right-1.5 w-3.5 h-3.5 text-amber-500" />}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{emp.employee_name}</p>
                          <p className="text-xs text-slate-400">{emp.department || 'No department'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 timer-display">{formatDecimalHours(emp.total_hours)}</p>
                          <p className="text-xs text-slate-400">{emp.entry_count} entries</p>
                        </div>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' : idx === 1 ? 'bg-slate-200 text-slate-600' : 'bg-orange-100 text-orange-600'
                        }`}>
                          {idx + 1}
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center">
                      <Trophy className="w-8 h-8 mx-auto text-slate-200 dark:text-slate-600 mb-2" />
                      <p className="text-sm text-slate-400">No tracking data this week</p>
                    </div>
                  )
                ) : (
                  needsAttention.length > 0 ? needsAttention.map((emp, idx) => (
                    <div key={emp.employee_id} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors" data-testid={`needs-attention-${idx}`}>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          {emp.employee_photo && <AvatarImage src={emp.employee_photo} />}
                          <AvatarFallback className={`text-xs font-semibold ${
                            emp.total_hours === 0 ? 'bg-red-100 dark:bg-red-900/30 text-red-600' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600'
                          }`}>
                            {getInitials(emp.employee_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{emp.employee_name}</p>
                          <p className="text-xs text-slate-400">{emp.department || 'No department'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {emp.total_hours === 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-semibold">
                            <AlertOctagon className="w-3 h-3" /> No tracking
                          </span>
                        ) : (
                          <p className="text-sm font-bold text-amber-600 dark:text-amber-400 timer-display">{formatDecimalHours(emp.total_hours)}</p>
                        )}
                        <p className="text-xs text-slate-400 mt-0.5">{emp.entry_count} entries</p>
                      </div>
                    </div>
                  )) : (
                    <div className="p-8 text-center">
                      <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                      <p className="text-sm text-slate-400">Everyone is tracking well!</p>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Recent Screenshots */}
            {recentScreenshots.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5" style={cardStyle} data-testid="recent-screenshots">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Camera className="w-4 h-4 text-slate-400" />
                    Recent Screenshots
                  </h3>
                  <Link to="/team-screenshots" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1" data-testid="view-all-screenshots-link">
                    View all <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                  {recentScreenshots.map((ss) => (
                    <div key={ss.id} className="group relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-700 aspect-video">
                      {screenshotUrls[ss.id] ? (
                        <img
                          src={screenshotUrls[ss.id]}
                          alt={`Screenshot by ${ss.employee_name}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Camera className="w-5 h-5 text-slate-300 dark:text-slate-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-[10px] font-semibold text-white truncate">{formatNameCompact(ss.employee_name)}</p>
                        <p className="text-[9px] text-white/70">{new Date(ss.captured_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Employee */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 overflow-hidden" style={cardStyle}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('dashboard.byEmployee')}</h3>
                <Link to="/admin/people" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  {t('common.viewAll')} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {employees.slice(0, 5).map(emp => (
                  <div key={emp.id} className="flex items-center justify-between px-6 py-3.5 hover:bg-slate-50 dark:hover:bg-slate-700/60 transition-colors">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        {emp.profile_photo && <AvatarImage src={emp.profile_photo} />}
                        <AvatarFallback className="bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 text-xs font-semibold">
                          {getInitials(emp.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-white">{emp.name}</p>
                        <p className="text-xs text-slate-400">{emp.department || 'No department'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-900 dark:text-white timer-display">{formatDecimalHours(emp.hours_this_week)}</p>
                        <p className="text-xs text-slate-400">{formatDecimalHours(emp.daily_average)}/day</p>
                      </div>
                      {emp.timesheet_status && emp.timesheet_status !== 'draft' && (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold border ${getStatusBadge(emp.timesheet_status)}`}>
                          {emp.timesheet_status}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-4 space-y-6">
            {/* Pending Actions */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 overflow-hidden" style={cardStyle}>
              <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 dark:border-slate-700">
                <Zap className="w-4 h-4 text-amber-500" />
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('dashboard.pendingActions')}</h3>
                {pendingActions.actions?.length > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold">{pendingActions.actions.length}</span>
                )}
              </div>
              <div className="divide-y divide-slate-50">
                {pendingActions.actions?.slice(0, showAllPending ? 8 : 3).map((action, idx) => (
                  <div key={idx} className="px-5 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5">
                        <Avatar className="h-7 w-7 mt-0.5">
                          {action.employee_photo && <AvatarImage src={action.employee_photo} />}
                          <AvatarFallback className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-semibold">
                            {getInitials(action.employee_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-white">{action.employee_name}</p>
                          <p className="text-xs text-slate-400">
                            {action.type === 'timesheet' && `Timesheet \u2022 ${action.days_waiting}d waiting`}
                            {action.type === 'time_off' && `${action.leave_type?.replace('_', ' ')} \u2022 ${action.working_days} days`}
                            {action.type === 'not_submitted' && 'Timesheet not submitted'}
                          </p>
                        </div>
                      </div>
                      <div>
                        {action.type === 'timesheet' && (
                          <Link to="/admin/approvals">
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-lg font-semibold">
                              <Eye className="w-3 h-3 mr-1" /> {t('dashboard.review')}
                            </Button>
                          </Link>
                        )}
                        {action.type === 'time_off' && (
                          <Link to="/admin/time-off">
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-lg font-semibold">
                              <Eye className="w-3 h-3 mr-1" /> {t('dashboard.review')}
                            </Button>
                          </Link>
                        )}
                        {action.type === 'not_submitted' && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-lg font-semibold"
                            onClick={() => handleSendReminder(action.employee_id, action.week_start)}>
                            <Send className="w-3 h-3 mr-1" /> {t('dashboard.remind')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {(!pendingActions.actions || pendingActions.actions.length === 0) && (
                  <div className="p-8 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-400 mb-2" />
                    <p className="text-sm text-slate-400 font-medium">{t('dashboard.allCaughtUp')}</p>
                  </div>
                )}
              </div>
              {pendingActions.actions?.length > 3 && (
                <button
                  onClick={() => setShowAllPending(!showAllPending)}
                  className="w-full px-5 py-2.5 text-xs font-semibold text-blue-600 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors flex items-center justify-center gap-1 border-t border-slate-100 dark:border-slate-700"
                  data-testid="toggle-pending-actions"
                >
                  {showAllPending ? (
                    <><ChevronUp className="w-3.5 h-3.5" /> Show less</>
                  ) : (
                    <><ChevronDown className="w-3.5 h-3.5" /> Show {pendingActions.actions.length - 3} more</>
                  )}
                </button>
              )}
            </div>

            {/* Today at a Glance */}
            {todayData && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5" style={cardStyle}>
                <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {t('dashboard.todayAtGlance')}
                </h3>
                <div className="space-y-3">
                  {todayData.on_leave?.length > 0 && (
                    <div className="p-3 bg-violet-50 dark:bg-violet-900/30 rounded-xl">
                      <p className="text-xs font-semibold text-violet-700 dark:text-violet-300 mb-2">{t('dashboard.onLeave')} ({todayData.on_leave.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {todayData.on_leave.map(emp => (
                          <span key={emp.id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 text-xs font-medium text-violet-700 dark:text-violet-300 border border-violet-200 dark:border-violet-600">
                            {formatNameCompact(emp.name)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {todayData.currently_tracking?.length > 0 && (
                    <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-2">{t('dashboard.currentlyTracking')} ({todayData.currently_tracking.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {todayData.currently_tracking.map(emp => (
                          <span key={emp.id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 text-xs font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-600">
                            {formatNameCompact(emp.name)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {todayData.not_tracked_today?.length > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/30 rounded-xl">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-2">{t('dashboard.notTrackedYet')} ({todayData.not_tracked_today.length})</p>
                      <div className="flex flex-wrap gap-1.5">
                        {todayData.not_tracked_today.slice(0, 6).map(emp => (
                          <span key={emp.id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 text-xs font-medium text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-600">
                            {formatNameCompact(emp.name)}
                          </span>
                        ))}
                        {todayData.not_tracked_today.length > 6 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 text-xs font-medium text-amber-600 dark:text-amber-300 border border-amber-200 dark:border-amber-600">
                            +{todayData.not_tracked_today.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Top Projects */}
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200/60 dark:border-slate-700 p-5" style={cardStyle}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-900 dark:text-white">{t('dashboard.topProjects')}</h3>
                <Link to="/admin/projects" className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  {t('common.viewAll')} <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="space-y-3.5">
                {projects.map(proj => (
                  <div key={proj.id} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: proj.color }} />
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate max-w-[140px]">{proj.name}</span>
                      </div>
                      <span className="text-sm font-bold text-slate-900 dark:text-white timer-display">{formatDecimalHours(proj.hours_this_week)}</span>
                    </div>
                    {proj.progress_percent !== null && (
                      <Progress value={Math.min(proj.progress_percent, 100)} className="h-1.5" />
                    )}
                  </div>
                ))}
                {projects.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">{t('dashboard.noProjectActivity')}</p>
                )}
              </div>
            </div>

            {/* Alerts */}
            {alerts.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-500/30 p-5" style={cardStyle}>
                <h3 className="text-base font-semibold text-red-600 flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4" />
                  {t('dashboard.alerts')}
                </h3>
                <div className="space-y-2">
                  {alerts.slice(0, 5).map((alert, idx) => (
                    <div key={idx}>
                      <Link to={alert.action_url || '#'}>
                        <div className={`p-3 rounded-xl flex items-start gap-2.5 hover:opacity-80 transition-opacity ${
                          alert.severity === 'error' ? 'bg-red-50 dark:bg-red-900/30' : 'bg-amber-50 dark:bg-amber-900/30'
                        }`}>
                          {alert.severity === 'error' ? (
                            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-700 dark:text-slate-200">{alert.message}</p>
                            {alert.type === 'no_tracking_group' && alert.employees?.length > 0 && (
                              <div className="mt-2">
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAlertDetails(!showAlertDetails); }}
                                  className="text-xs text-amber-600 dark:text-amber-400 font-semibold hover:underline flex items-center gap-1"
                                >
                                  {showAlertDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  {showAlertDetails ? 'Hide names' : 'View names'}
                                </button>
                                {showAlertDetails && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {alert.employees.map(emp => (
                                      <span key={emp.id} className="inline-flex items-center px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 text-xs font-medium text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-600">
                                        {formatNameCompact(emp.name)}
                                      </span>
                                    ))}
                                    {alert.count > 5 && (
                                      <span className="text-xs text-amber-500">+{alert.count - 5} more</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
